import { useEffect, useState } from 'react';
import { fetchAllAirtableRecords } from '../services/airtable';
import { FALLBACK_CLIENTS_TABLE_ID, clientFields } from '../constants/formConfig';

// Stable fallback to avoid recreating the default options array on each render.
const EMPTY_CLIENT_OPTIONS = [];

const ACTIVE_CLIENT_FIELD_CANDIDATES = ['Activo', 'ACTIVO', 'Cliente Activo', 'clienteActivo', 'active'];

const normalizeBooleanFlag = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  if (['true', '1', 'si', 'sí', 'yes', 'activo', 'activa', 'vigente', 'enabled'].includes(normalized)) return true;
  if (['false', '0', 'no', 'inactivo', 'inactive', 'disabled', 'baja'].includes(normalized)) return false;
  return null;
};

const resolveClientIsActive = fields => {
  for (const fieldName of ACTIVE_CLIENT_FIELD_CANDIDATES) {
    if (!(fieldName in (fields || {}))) continue;
    const normalized = normalizeBooleanFlag(fields[fieldName]);
    if (normalized !== null) return normalized;
  }

  const statusField = fields?.Estatus ?? fields?.ESTATUS ?? fields?.Estado ?? fields?.estado;
  const statusFlag = normalizeBooleanFlag(statusField);
  if (statusFlag !== null) return statusFlag;

  return Boolean(fields?.[clientFields.enlaceFormulario]);
};

export const normalizeListaCtesId = value => {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value !== 'string') {
    return String(value);
  }
  const trimmed = value.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
};

/**
 * Carga el directorio de clientes (ID -> nombre legible) y el listado para selects.
 */
export function useClientesDirectory({
  enabled,
  clientsTableId,
  mockClientOptions = EMPTY_CLIENT_OPTIONS
}) {
  const [clientOptions, setClientOptions] = useState([]);
  const [clientDirectory, setClientDirectory] = useState({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setClientOptions(mockClientOptions);
      setClientDirectory({});
      setClientsError('');
      setLoadingClients(false);
      return;
    }
    const resolvedTableId = clientsTableId || FALLBACK_CLIENTS_TABLE_ID;
    if (!resolvedTableId) return;

    let ignore = false;
    async function loadClients() {
      try {
        setLoadingClients(true);
        setClientsError('');
        const records = await fetchAllAirtableRecords({
          tableId: resolvedTableId,
          maxRecords: 500
        });
        if (ignore) return;
        const directory = {};
        const options = records
          .map(record => {
            const fields = record.fields || {};
            const empresa = fields.Empresa || '';
            const alias = fields.Alias || '';
            const nombreComercial = fields['NOMBRE COMERCIAL'] || '';
            const idCliente = normalizeListaCtesId(fields['ID Cliente']);
            const rfc = fields.RFC || fields.Rfc || fields.rfc || '';
            const isActive = resolveClientIsActive(fields);
            const name =
              empresa ||
              alias ||
              nombreComercial ||
              idCliente ||
              record.id;
            directory[record.id] = {
              id: record.id,
              name,
              empresa,
              alias,
              nombreComercial,
              idCliente,
              rfc,
              isActive
            };
            if (!fields[clientFields.enlaceFormulario]) return null;
            return {
              id: record.id,
              alias,
              empresa,
              nombreComercial,
              idCliente,
              rfc,
              isActive
            };
          })
          .filter(Boolean);
        const sorted = options.sort((a, b) => {
          const nameA = (a.alias || a.empresa || '').toLowerCase();
          const nameB = (b.alias || b.empresa || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        setClientOptions(sorted);
        setClientDirectory(directory);
      } catch (error) {
        if (!ignore) {
          setClientsError(error.message);
          setClientOptions(mockClientOptions);
        }
      } finally {
        if (!ignore) {
          setLoadingClients(false);
        }
      }
    }

    loadClients();
    return () => {
      ignore = true;
    };
  }, [enabled, clientsTableId, mockClientOptions]);

  return {
    clientOptions,
    clientDirectory,
    loadingClients,
    clientsError
  };
}
